// src/Components/TopicsSection.js
import React, { useState, useMemo, useEffect } from 'react';

// Activity Imports
import QuizActivity from "../../../Shared/Activities/QuizActivity.jsx";
import VocabMatch from "../../../Shared/Activities/VocabMatch.jsx";
import ReadingActivity from "../../../Shared/Activities/ReadingActivity.jsx";
import Videos from "../../../Shared/Activities/VideoActivity.jsx";
import DiscussionQuestions from "../../../Shared/Activities/DiscussionQuestions.jsx"
import TouchCategorizationExercise from "../../../Shared/Activities/sorting.jsx";
import Flashcards from "../../Flashcards/Flashcard.jsx"

import "./TopicsSection.css";

const QUIZ_TYPES = ["quiz", "grammarquiz", "triviaquiz"];
const ALL_TOPICS = "All Topics";
const ALL_LEVELS = "All Levels";

const TopicsSection = ({ type, data }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [showResults, setShowResults] = useState(false);
  // Lesson plans pass the lesson name in data.topic; start on
  // that lesson's questions when the pool contains them,
  // otherwise show the whole pool.
  const [topicFilter, setTopicFilter] = useState(() => {
    if (!data?.topic) return ALL_TOPICS;
    const topics = new Set(
      (data.questions || []).map((q) => q.topic).filter(Boolean)
    );
    return topics.has(data.topic) ? data.topic : ALL_TOPICS;
  });
  const [levelFilter, setLevelFilter] = useState(ALL_LEVELS);

  const isQuiz = QUIZ_TYPES.includes(type);

  const allQuestions = useMemo(
    () => (isQuiz && Array.isArray(data?.questions) ? data.questions : []),
    [isQuiz, data]
  );

  // Teacher-facing filters. Only options that exist in the data are offered,
  // so a dropdown never appears with nothing to select.
  const topicOptions = useMemo(
    () => [ALL_TOPICS, ...Array.from(new Set(allQuestions.map((q) => q.topic).filter(Boolean)))],
    [allQuestions]
  );

  // Questions carry a `level` in the JSON; the dropdown fills itself in.
  const levelOptions = useMemo(
    () => [ALL_LEVELS, ...Array.from(new Set(allQuestions.map((q) => q.level).filter(Boolean)))],
    [allQuestions]
  );

  const questions = useMemo(
    () =>
      allQuestions.filter(
        (q) =>
          (topicFilter === ALL_TOPICS || q.topic === topicFilter) &&
          (levelFilter === ALL_LEVELS || q.level === levelFilter)
      ),
    [allQuestions, topicFilter, levelFilter]
  );

  // Changing a filter starts a fresh run.
  useEffect(() => {
    setCurrentIndex(0);
    setScore(0);
    setShowResults(false);
  }, [topicFilter, levelFilter]);

  if (!data) return null;

  const handleQuizAnswer = (result) => {
    if (result.correct) setScore((prev) => prev + 1);

    if (currentIndex + 1 < questions.length) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      setShowResults(true);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const resetQuiz = () => {
    setCurrentIndex(0);
    setScore(0);
    setShowResults(false);
  };

  const showTopicFilter = isQuiz && topicOptions.length > 1;
  const showLevelFilter = isQuiz && levelOptions.length > 1;
  const showFilters = (showTopicFilter || showLevelFilter) && !showResults;

  const getQuizTitle = () => data.grammarQuizTitle || data.quizTitle || "Quiz";

  const renderContent = () => {
    if (showResults && isQuiz) {
      return (
        <div className="quiz-results-card">
          <h2>Quiz Completed!</h2>
          <p>Your Score: {score} / {questions.length}</p>
          <button onClick={resetQuiz} className="control-btn">
            Try Again
          </button>
        </div>
      );
    }

    switch (type) {
      case "quiz":
      case "grammarquiz":
      case "triviaquiz":
        if (questions.length === 0) {
          return <p className="quiz-nothing-to-show">No questions match the selected filters.</p>;
        }
        return (
          <QuizActivity
            key={currentIndex} // Resets internal state (shuffling/selection) per question
            question={questions[currentIndex]}
            questionIndex={currentIndex}
            totalQuestions={questions.length}
            onAnswer={handleQuizAnswer}
            onPrev={handlePrev}
          />
        );

      case "vocab": {
        // Build a set from the inline vocab section
        const setId = data.topic?.toLowerCase().replace(/\s+/g, '-') || 'vocab';
        const set = {
          id: setId,
          title: data.vocabTitle || 'Vocabulary',
          topic: data.topic || 'General',
          level: data.level || '',
          cards: data.cards || [],
        };

        return (
          <Flashcards
            sets={[set]}
            initialSetId={setId}
            onComplete={(results) => {
              // Optional: handle results here
            }}
          />
        );
      }

      case "reading":
        return <ReadingActivity data={data} title={data.readingcompTitle} skipSEO />;

      case "video":
        return <Videos data={data} skipSEO />;

      case "discussion":
        return <DiscussionQuestions questions={data.questions} title={data.discussionTitle} />;

      case "categorization":
        // Optional: render a title if provided in the section data
        if (data.title) {
          return (
            <>
              <h2 className="topics-section-title">{data.title}</h2>
              <TouchCategorizationExercise
                data={data.data}
                categories={data.categories}
                topic={data.topic}
                freeForm={data.freeForm ?? false}
              />
            </>
          );
        }
        return <TouchCategorizationExercise
          data={data.data}
          categories={data.categories}
          topic={data.topic}
        />;

      default:
        return <div>Component for {type} not found.</div>;
    }
  };

  return (
    <div className="topics-section-container" style={{ marginBottom: '40px' }}>
      

      {showFilters && (
        <div className="quiz-filters">
          {showTopicFilter && (
            <label className="quiz-filter">
              <span className="quiz-filter__label">Topic</span>
              <select
                className="quiz-filter__select"
                value={topicFilter}
                onChange={(e) => setTopicFilter(e.target.value)}
              >
                {topicOptions.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>
          )}

          {showLevelFilter && (
            <label className="quiz-filter">
              <span className="quiz-filter__label">Level</span>
              <select
                className="quiz-filter__select"
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
              >
                {levelOptions.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}

      <div className="topics-section-content">
        {renderContent()}
      </div>
    </div>
  );
};

export default TopicsSection;
